import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { LogIn, LogOut, Clock, AlertTriangle, ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const getKioskToken = (clockCode) => localStorage.getItem(`kiosk_token_${clockCode}`);

// Extract a human-readable string from FastAPI's `detail` payload, which can
// be either a plain string or a structured `{code, message}` object (kiosk
// endpoints use the object form so the client can react to specific error
// codes without string-matching).
const detailMessage = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d || fallback;
  if (d && typeof d === "object" && d.message) return d.message;
  return fallback;
};

const kioskHeaders = (clockCode) => {
  const token = getKioskToken(clockCode);
  return token ? { "X-Kiosk-Token": token } : {};
};

const TimeClock = () => {
  const { clockCode } = useParams();
  const [company, setCompany] = useState(null);
  const [pin, setPin] = useState("");
  const [isRegistered, setIsRegistered] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [planGated, setPlanGated] = useState(false);
  const [result, setResult] = useState(null);
  const [breakHours, setBreakHours] = useState("0");
  // Self-registration via owner-issued code.
  const [regCodeInput, setRegCodeInput] = useState("");
  const [regDeviceName, setRegDeviceName] = useState("");
  const [registering, setRegistering] = useState(false);
  const [regError, setRegError] = useState("");

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const res = await axios.get(`${API}/api/public/clock/${clockCode}`);
        setCompany(res.data);
        // Validate kiosk token against backend
        const token = getKioskToken(clockCode);
        if (token) {
          try {
            // Probe token validity by attempting verify with a dummy PIN.
            // 401 = token accepted, wrong PIN (registered).
            // 403 with code=device_not_registered = token truly bad -> clear.
            // Any other 403 (e.g. code=ip_not_allowed) means the token is FINE
            // but this network is blocked — do NOT wipe the token or the
            // kiosk will loop back to the register screen forever after the
            // owner tweaks the IP whitelist.
            await axios.post(`${API}/api/public/clock/${clockCode}/verify`, { pin: "0000" }, { headers: { "X-Kiosk-Token": token } });
            setIsRegistered(true);
          } catch (err) {
            const status = err.response?.status;
            const detail = err.response?.data?.detail;
            const code = typeof detail === "object" ? detail?.code : null;
            const message = typeof detail === "string" ? detail : (detail?.message || "");
            const isTokenInvalid =
              status === 403 &&
              (code === "device_not_registered" ||
                /unregistered device/i.test(message));
            if (isTokenInvalid) {
              localStorage.removeItem(`kiosk_token_${clockCode}`);
              setIsRegistered(false);
            } else {
              // Token is fine — either wrong PIN (401), IP-blocked (403), or
              // transient network error. Keep the token so the kiosk stays
              // registered; the PIN screen will surface the real error when
              // the employee tries their PIN.
              setIsRegistered(true);
            }
          }
        }
      } catch (err) {
        if (err.response?.status === 402) {
          setPlanGated(true);
          setError(detailMessage(err, "Kiosk clock-in requires the Starter plan."));
        } else {
          setError("Invalid time clock link");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchCompany();
  }, [clockCode]);

  const handleVerify = async () => {
    if (pin.length < 4 || pin.length > 6) return;
    setError("");
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/api/public/clock/${clockCode}/verify`, { pin }, { headers: kioskHeaders(clockCode) });
      setEmployee(res.data);
      setResult(null);
    } catch (err) {
      setError(detailMessage(err, "Invalid PIN"));
      setEmployee(null);
    } finally {
      setActionLoading(false);
    }
  };

  const handleClockIn = async () => {
    setActionLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/api/public/clock/${clockCode}/checkin`, { pin }, { headers: kioskHeaders(clockCode) });
      setResult({ type: "in", ...res.data });
      setEmployee(prev => ({ ...prev, clocked_in: true, check_in_time: res.data.check_in }));
    } catch (err) {
      setError(detailMessage(err, "Failed to clock in"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleClockOut = async () => {
    setActionLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/api/public/clock/${clockCode}/checkout`, { pin, break_hours: parseFloat(breakHours || 0) }, { headers: kioskHeaders(clockCode) });
      setResult({ type: "out", ...res.data });
      setEmployee(prev => ({ ...prev, clocked_in: false, check_in_time: null }));
    } catch (err) {
      setError(detailMessage(err, "Failed to clock out"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReset = () => {
    setPin("");
    setEmployee(null);
    setResult(null);
    setError("");
    setBreakHours("0");
  };

  const handleSelfRegister = async () => {
    setRegError("");
    if (!/^\d{6}$/.test(regCodeInput)) {
      setRegError("Enter the 6-digit code from your manager.");
      return;
    }
    setRegistering(true);
    try {
      const res = await axios.post(`${API}/api/public/clock/${clockCode}/register`, {
        code: regCodeInput,
        device_name: (regDeviceName || "").trim() || "Kiosk",
      });
      localStorage.setItem(`kiosk_token_${clockCode}`, res.data.device_token);
      setIsRegistered(true);
      setRegCodeInput("");
      setRegDeviceName("");
    } catch (err) {
      setRegError(detailMessage(err, "Could not register device. Try again."));
    } finally {
      setRegistering(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A192F] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#0D9488] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A192F] flex items-center justify-center p-4">
      {planGated ? (
        <Card className="w-full max-w-md border-0 shadow-2xl" data-testid="time-clock-gated">
          <CardContent className="p-8 text-center space-y-4">
            <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto">
              <Clock className="w-7 h-7 text-amber-600" />
            </div>
            <h1 className="text-xl font-bold text-[#0A192F] font-['Manrope']">Kiosk clock-in is a Starter feature</h1>
            <p className="text-sm text-slate-600">{error}</p>
            <p className="text-xs text-slate-400 pt-2">
              Please contact your business owner or manager to upgrade the plan and enable the kiosk.
            </p>
          </CardContent>
        </Card>
      ) : (
      <Card className="w-full max-w-md border-0 shadow-2xl" data-testid="time-clock-page">
        <CardContent className="p-8">
          {/* Company Header */}
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 overflow-hidden bg-white border border-slate-200" data-testid="timeclock-brand-mark">
              {company?.logo ? (
                <img src={company.logo} alt="" className="max-w-full max-h-full object-contain" data-testid="timeclock-tenant-logo" />
              ) : (
                <div className="w-14 h-14 bg-[#0D9488] flex items-center justify-center">
                  <Clock className="w-7 h-7 text-white" />
                </div>
              )}
            </div>
            <h1 className="text-xl font-bold text-[#0A192F] font-['Manrope']">{company?.company_name || "Time Clock"}</h1>
            <p className="text-sm text-slate-500 mt-1">
              {isRegistered
                ? "Enter your PIN to clock in or out"
                : "Register this device to activate the time clock"}
            </p>
            {isRegistered && (
              <div className="flex items-center gap-1 justify-center mt-2">
                <ShieldCheck className="w-3.5 h-3.5 text-[#10B981]" />
                <span className="text-xs text-[#10B981] font-medium">Registered Device</span>
              </div>
            )}
          </div>

          {/* Unregistered devices: show ONLY the register form. The PIN
              input is intentionally hidden until the device is trusted so
              employees don't waste a PIN attempt on a device that can't
              verify anything. */}
          {!isRegistered && (
            <div className="mb-4 p-5 rounded-lg border border-[#0D9488]/30 bg-[#0D9488]/5 space-y-3" data-testid="self-register-form">
              <div className="text-center mb-1">
                <div className="w-10 h-10 rounded-full bg-white border border-[#0D9488]/30 flex items-center justify-center mx-auto mb-2">
                  <ShieldCheck className="w-5 h-5 text-[#0D9488]" />
                </div>
                <p className="font-semibold text-[#0A192F]">Register this device</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Ask your manager for the 6-digit registration code, then give this device a name.
                </p>
              </div>
              <Input
                type="text"
                inputMode="numeric"
                placeholder="6-digit code"
                value={regCodeInput}
                onChange={(e) => setRegCodeInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
                maxLength={6}
                className="text-center tracking-[0.4em] font-mono text-lg h-12"
                autoFocus
                data-testid="reg-code-input"
              />
              <Input
                type="text"
                placeholder="Device name (e.g., Front Counter iPad)"
                value={regDeviceName}
                onChange={(e) => setRegDeviceName(e.target.value)}
                data-testid="reg-device-name-input"
              />
              {regError && (
                <div className="text-sm text-red-600 flex items-center gap-2" data-testid="reg-error">
                  <AlertTriangle className="w-4 h-4" /> {regError}
                </div>
              )}
              <Button
                className="w-full bg-[#0D9488] hover:bg-[#14B8A6] h-11"
                onClick={handleSelfRegister}
                disabled={registering || regCodeInput.length !== 6}
                data-testid="reg-submit-btn"
              >
                {registering ? "Registering…" : "Register Device"}
              </Button>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-600 flex items-center gap-2" data-testid="clock-error">
              <AlertTriangle className="w-4 h-4" /> {error}
            </div>
          )}

          {/* Result Message */}
          {result && (
            <div className={`mb-6 p-4 rounded-lg text-center ${result.type === "in" ? "bg-emerald-50 border border-emerald-100" : "bg-blue-50 border border-blue-100"}`} data-testid="clock-result">
              <p className={`font-semibold ${result.type === "in" ? "text-emerald-700" : "text-blue-700"}`}>
                {result.type === "in" ? "Clocked In!" : "Clocked Out!"}
              </p>
              <p className="text-sm text-slate-600 mt-1">{result.name}</p>
              {result.type === "out" && (
                <p className="text-sm text-slate-500 mt-1">
                  Worked: {result.worked_hours?.toFixed(1)} hrs | Net: {result.net_hours?.toFixed(1)} hrs
                </p>
              )}
              <Button variant="outline" size="sm" className="mt-3" onClick={handleReset} data-testid="clock-reset-btn">
                Done
              </Button>
            </div>
          )}

          {/* PIN Entry — only shown once the device is registered so we
              have a valid X-Kiosk-Token to send with /verify. */}
          {isRegistered && !employee && !result && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="Enter your PIN (4-6 digits)"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="text-center text-2xl tracking-[0.5em] h-14 font-mono"
                  autoFocus
                  data-testid="pin-input"
                />
              </div>
              <Button
                className="w-full bg-[#0D9488] hover:bg-[#14B8A6] h-12"
                disabled={pin.length < 4 || pin.length > 6 || actionLoading}
                onClick={handleVerify}
                data-testid="pin-submit-btn"
              >
                {actionLoading ? "Verifying..." : "Submit"}
              </Button>
            </div>
          )}

          {/* Clock In/Out Actions */}
          {employee && !result && (
            <div className="space-y-4">
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-lg font-semibold text-[#0A192F]">{employee.name}</p>
                {employee.clocked_in ? (
                  <Badge className="bg-emerald-100 text-emerald-700 mt-1">Clocked In since {formatTime(employee.check_in_time)}</Badge>
                ) : (
                  <Badge variant="outline" className="text-slate-500 mt-1">Not clocked in</Badge>
                )}
              </div>

              {employee.clocked_in ? (
                <div className="space-y-3">
                  <div className="space-y-1">
                    <label className="text-xs text-slate-500 font-medium">Break Hours (optional)</label>
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      max="4"
                      value={breakHours}
                      onChange={(e) => setBreakHours(e.target.value)}
                      className="h-10"
                      data-testid="break-hours-input"
                    />
                  </div>
                  <Button className="w-full bg-[#E11D48] hover:bg-[#BE123C] h-12" disabled={actionLoading} onClick={handleClockOut} data-testid="clock-out-btn">
                    <LogOut className="w-5 h-5 mr-2" /> {actionLoading ? "Clocking out..." : "Clock Out"}
                  </Button>
                </div>
              ) : (
                <Button className="w-full bg-[#10B981] hover:bg-[#059669] h-12" disabled={actionLoading} onClick={handleClockIn} data-testid="clock-in-btn">
                  <LogIn className="w-5 h-5 mr-2" /> {actionLoading ? "Clocking in..." : "Clock In"}
                </Button>
              )}
              <Button variant="ghost" className="w-full text-slate-500" onClick={handleReset} data-testid="clock-back-btn">
                Back
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
};

export default TimeClock;
